// Shared mutable game state.
//
// The original single-file build kept all of this in module-scope `let`
// variables. Splitting the game into modules means that state now has to live
// somewhere both physics and rendering can reach, so it is collected into one
// object that is mutated in place (never reassigned).

import { TARGET_WINS_DEFAULT } from './config.js';

export const state = {
  // --- world ---------------------------------------------------------------
  /** Solid buildings, each with its own pre-rendered canvas we punch holes in. */
  buildings: [],
  /** Background silhouettes and star field, regenerated once per round. */
  skyline: [],
  stars: [],
  /** This round's moon, or null for a moonless round. See moon.js. */
  moon: null,
  /** A jump in progress: { idx, frames, target }. See leap.js. */
  leap: null,

  /**
   * The sheep mid-walk: { x, y, vx, vy, dir, hops, startAt }. It is not a projectile — it
   * does not fly, it hops, and it is the only thing in the game that moves on its own
   * after the turn has been spent. See launchSheep() in flow.js.
   */
  sheep: null,
  /** Each side's own angle and power, so one cannot dial the other's shot. */
  aim: null,
  /**
   * Which way each side looks: +1 right, -1 left. State rather than a function of
   * the index, because a swap can put them on each other's rooftop and they have to
   * turn round. Everything that mirrors anything reads this — the drawing and the
   * hit test both — which is what keeps them in step.
   */
  facing: [1, -1],
  /** Whether the two of them have traded places. One bit, and a match code can carry it. */
  swapped: false,
  /**
   * Whether anything has been spent in this match: an ult fired, a defence put up.
   *
   * A fact about the match rather than a counter, so it is cleared where a match starts and read
   * where one ends — the achievement that wants it is "won without spending a thing", and it has
   * to mean this match and not the lifetime total.
   */
  spentInMatch: false,
  /** The shield a side has raised, and whose it is: { idx, at }. See config.js. */
  shield: null,
  /** A planted spring, same shape: { idx, at }. */
  spring: null,
  /** A mushroom waiting to save somebody: { idx }. */
  mushroom: null,
  /**
   * El brillo del superguerrero, `{ idx, until }`, iba aquí. El poder se quitó —bloqueaba el
   * turno que lo activaba— y con él sus dos campos. Lo que dibujaba el campo sigue en
   * render/gorilla.js y no pinta nada: lee esto, que ya no existe, y con `undefined` no hay
   * hueco que rellenar. Ver SPECIALS en progress.js.
   */
  /** This round's reward crate, or null. See crates.js. */
  crate: null,
  /**
   * Las vidas extra que le quedan a cada lado, 0 o más.
   *
   * Aguanta un golpe mortal: cuando el plátano, la granada o la bomba del dron iban a acabar
   * con él, se gasta una y la ronda sigue. Son de la PARTIDA y no de la ronda —`makeCity` no
   * las toca, como no toca las cargas— y llegan de la caja del cielo. Ver `collectCrate` y
   * `spendLife`. El tope lo pone `LIFE_CAP` en config.js.
   */
  lives: [0, 0],
  /**
   * Si la caja de esta ronda ya la ha abierto alguien, la haya abierto quien la haya abierto.
   *
   * Es distinto de `crate === null`, que también es cierto cuando la ronda no trajo caja y cuando
   * la que trajo se cayó al hueco entre dos torres: esto dice una cosa sola, y es la que tiene que
   * viajar en el código — ver `k` en matchcode.js.
   */
  crateTaken: false,
  /** Streaks of light crossing the sky, and the station on its rare pass. */
  meteors: [],
  iss: null,
  nextMeteor: 0,
  nextIss: 0,
  /** A rocket on its way up, or a booster on its way back down. */
  rocket: null,
  nextRocket: 0,
  /** A light aircraft towing a banner, with whatever it is advertising. */
  banner: null,
  nextBanner: 0,
  /** The presidential aircraft and its two escorts, on its occasional pass. */
  airforce: null,
  nextAirforce: 0,
  /** The bat-signal, on the rare night somebody switches it on. */
  batsignal: null,
  nextBatsignal: 0,
  /**
   * The reconnaissance drone on its pass, or null. Unlike the sky traffic this
   * one is a weapon, so it is not gated on the mode having a sky at all.
   */
  drone: null,
  /**
   * The bomb the drone has dropped, as { x, y, vy, victim }, or null.
   *
   * It is not a projectile: it is not thrown, nobody aims it and it does not pass the turn.
   * It falls straight down onto the gorilla the drone painted, and that is the whole of it.
   */
  bomb: null,
  /** Which side the drone is painting for (-1 for nobody), and until when. */
  paintedFor: -1,
  paintedUntil: 0,
  /** What the gorillas are shouting, as { idx, text, born, ms }. */
  bubbles: [],
  /**
   * The kill caption, as { at, tip }, or null. One at a time: two gorillas cannot
   * die in the same round.
   */
  wasted: null,
  /**
   * The round announcement, as { n, at }, or null. Read by the renderer and
   * derived from `clock`, so a pause does not leave ROUND 3 hanging on screen
   * for as long as the pause lasted.
   */
  announce: null,
  /**
   * The nudge that says whose turn has just begun, as { side, at, machine }, or null.
   *
   * Its own slot rather than the announcement's, because once a round the two are
   * due at the same moment and the round call has the right of way: the nudge waits
   * for it instead of overwriting it. Derived from `clock` for the reason above.
   *
   * `machine` is the turn having gone to the CPU. It is cued and not announced: the
   * sound is for the player who has just lost the board, and there is nobody to tell
   * that the CPU's turn has come round.
   */
  turnCall: null,
  /** Rain or snow, when the mode asks for it. */
  precip: [],
  /** Which of the two `precip` currently holds, so it is only rebuilt on change. */
  precipKind: null,
  /** When the storm mode next changes its mind about the wind. */
  nextGust: 0,
  /** The two gorillas: { x, y, alive }. */
  players: [],
  /** Horizontal wind, roughly -windMax..windMax. */
  wind: 0,

  // --- modes ---------------------------------------------------------------
  /** Which card the setup screen last applied, e.g. 'modern'. */
  mode: null,
  /** Resolved settings for the current match; see modes.js. */
  mods: null,
  /**
   * Where the front-end is: 'splash', 'title', 'menu', 'setup', 'game',
   * 'pause' or 'results'. The canvas keeps rendering behind all of them.
   */
  screen: 'splash',
  /**
   * What each side is: { species, worn } — an index into SPECIES, and which
   * accessory it has on in each slot.
   */
  looks: [
    { species: 0, worn: {} },
    { species: 1, worn: {} },
  ],
  /**
   * Seed every round is derived from. Sharing this plus the round number is
   * enough to rebuild the same city and wind on another machine.
   */
  matchSeed: '',

  // --- match ---------------------------------------------------------------
  /** 0 = player 1, 1 = player 2 / CPU. */
  turn: 0,
  /** When the current turn began, so a long wait can send the other one to sleep. */
  turnStartedAt: 0,
  /**
   * When the turn clock was last held, or 0 while it is running.
   *
   * A clock measured from `turnStartedAt` alone would keep counting through a
   * pause and through the equipment menu, which is the same as taking a turn away
   * for reading. The held stretch is added back to `turnStartedAt` when the clock
   * is let go, so the countdown simply does not move while the player is not able
   * to act.
   *
   * Set and cleared by advanceTurnClock, which works out whether the player can
   * act from the screen and the panels on every frame. Nothing else takes hold of
   * it: a flag with two owners is a flag that gets dropped by one of them.
   */
  turnHoldSince: 0,
  /**
   * El último segundo entero al que se le ha tocado el tic de aviso, o 0.
   *
   * Lo lleva el reloj para no tocar una vez por fotograma: entre 4.99 s y 4.01 s el segundo entero
   * es el mismo, y sin esta marca el aviso sería un zumbido en vez de un tic por segundo. Lo pone a
   * cero `startTurnClock`. Ver `playTurnTick` en audio.js.
   */
  turnTickAt: 0,
  /**
   * Si hay un panel abierto encima del campo: el zurrón, los logros, los personajes, las tablas.
   *
   * Lo escriben `openPanel` y `closePanels` (dom.js), que son los dos únicos sitios que tocan los
   * paneles, y lo lee `matchOnHold` (screens.js) — que es quien decide si la partida corre. Está
   * aquí y no en una consulta al DOM porque `screens.js` no puede importar `dom.js` (dom.js ya
   * importa screens.js), y porque preguntarlo en dos sitios es la forma de que los dos discrepen:
   * mientras esto se resolvía en un único punto el reloj se paraba con el zurrón delante y la caja
   * seguía cayendo por debajo.
   */
  panelOpen: false,
  round: 1,
  /**
   * Rondas ganadas, y no "Hits landed this round", que es lo que decía aquí.
   *
   * Cada impacto acaba la ronda, así que este contador y `matchWins` son el mismo número — suben
   * juntos en `endRound` y ninguno se reinicia por ronda —, y quien lo lee lo lee como el marcador:
   * la CPU decide si va por detrás con él. Se queda porque viaja en el código con el nombre `h` y
   * quitar el campo cambiaría la forma de un código ya escrito.
   */
  score: [0, 0],
  /**
   * The special each side is about to throw, or null for none — one per side.
   *
   * Per side for the same reason as the aim, and then one more: the CPU has a kit
   * of its own now, and a single shared slot meant the CPU's shot had to be told
   * to ignore whatever the player had selected. With a slot each there is nothing
   * to ignore — the selection that fires is always the thrower's own.
   */
  special: [null, null],
  /**
   * And the defence each side is holding, in a slot of its own.
   *
   * One slot for both meant that arming an attack quietly threw the shield away, and
   * the only trace was the other button's label — which is not the button you are
   * looking at when you have just come out of the panel. Two slots, and the turn is
   * still spent on one of them: that a defence costs the whole turn is its price.
   */
  guard: [null, null],
  /** Charges left, one map per side. */
  specialUses: [{}, {}],
  /** Rounds won this match. */
  matchWins: [0, 0],
  /**
   * Rounds a match is won by, or 0 for a match that does not end.
   *
   * Taken from the setup when the match is built and carried by the match from then on, so the two
   * machines of a duel are playing to the same finish line and moving the setting mid-match does not
   * move it. Every code carries it — the long one each turn and the short one the invitation is.
   */
  targetWins: TARGET_WINS_DEFAULT,
  /**
   * El reloj de la partida en curso, en segundos, y 0 para la que no tiene. Se copia del ajuste al
   * empezarla — como las rondas — y de ahí en adelante manda esto: cambiarlo en AJUSTES no mueve el
   * reloj de una partida que ya está en marcha, y el código de partida lo lleva para que las dos
   * máquinas cuenten lo mismo. Ver `newMatch` en flow.js y `secondsLeft` en dom.js.
   */
  turnSeconds: 0,
  matchOver: false,
  /**
   * A match is under way and can be gone back to. False until one starts, so the
   * menu does not offer to resume a match nobody has played yet.
   */
  matchLive: false,
  /**
   * La partida que empieza sola, contra la máquina.
   *
   * Por defecto sí: es lo que se juega sin tener que decidir nada, y quien quiera un duelo o un
   * rival del pozo tiene los botones ahí al lado para decirlo. Con esto en `true`, el JUGAR YA de la
   * portada y el EMPEZAR PARTIDA de los ajustes arrancan contra la CPU en vez de dejar la pantalla
   * pidiendo una elección. `against` ya vale 'cpu', que es la otra mitad de lo mismo.
   */
  cpuOn: true,
  /**
   * A rival over the network rather than the machine, and which side of the two this
   * machine is. `seat` is 0 everywhere else — in a hot-seat duel both sides are local and
   * nobody needs asking — and 1 for whoever answered a random match.
   */
  online: false,
  seat: 0,
  /**
   * A duel played by correspondence rather than over the pool: one person per machine, the
   * match travelling as a code, and the side that is not here never played by the machine.
   *
   * It is what "two players, one phone each" means, and it is a fact of its own because the
   * code has to say which side wrote it — see `d` in matchcode.js. A code written without it
   * reaches the other machine as an ordinary single-machine match, and that machine then plays
   * the side that is not there: the CPU, on somebody else's turn.
   */
  byCode: false,
  /**
   * The room this match is being played in: its code, or '' when it is not a room.
   *
   * A room is a code that stays good — the same one opens it all evening — so this is what
   * tells the difference between a duel by correspondence and one being played down a pipe.
   */
  room: '',
  /**
   * A duel whose second player is not here yet. On the board that side is not drawn at all: a
   * gorilla standing there would be wearing whatever this machine has saved for player two,
   * which is not a character anybody chose, and it is not a rival — the invitation has not been
   * answered. It goes up when the invitation goes out and comes down the moment somebody is
   * there to play it, which is also what leaves the pause's CONTINUAR out of action: a match
   * with nobody on the other side is a match there is nothing to go back to.
   */
  awaitingRival: false,
  /**
   * Whether the match's start has been held back. Set when a city is built while there is nobody
   * in the other seat, and answered the moment somebody arrives: the round call and the fanfare
   * belong to the moment the match can really begin.
   */
  callPending: false,
  /** What the rival is called, on the scoreboard. */
  rival: '',
  /**
   * Who the single player is up against: 'cpu' or 'random'. It is the question the setup
   * screen asks under one player, and the difficulty row below it is only its follow-up.
   */
  against: 'cpu',
  /**
   * With two players, where the other one is: 'code' for one phone each, 'hotseat' for the two
   * of them at one keyboard. The setup asks it under two players the way `against` is asked
   * under one, and 'code' is what it starts on — a duel with somebody who is not in the room
   * cannot be arranged any other way, and it is the one that needs the game to say something.
   */
  duel: 'code',

  // --- simulation ----------------------------------------------------------
  /** In-flight banana: { x, y, vx, vy, rot }, or null when nobody is shooting. */
  projectile: null,
  /** requestAnimationFrame handle for the render loop. */
  raf: 0,
  /** Pending setTimeout handles, so a new match never inherits stale ones. */
  cpuTimer: null,
  roundTimer: null,
  debris: [],
  fireballs: [],
  smoke: [],
  /** Expanding blast rings, drawn on top of everything else. */
  shockwaves: [],
  /**
   * Every crater punched into a building this round, as {x, y, r}. A shared
   * match transmits these, because they are holes in the buildings and cannot be
   * derived from the seed.
   */
  craters: [],
  /** Screen shake amplitude in pixels. */
  shake: 0,
  /** White flash opacity. */
  flash: 0,

  // --- timing ---------------------------------------------------------------
  /** Milliseconds since page load, refreshed once per rendered frame. */
  clock: 0,

  // --- audio ---------------------------------------------------------------
  audioCtx: null,
  /** On by default and switched on at "press start", the way a cabinet does it. */
  musicOn: true,
  /** The two levels, 0 to 1. Both start at 1, which is the mix the game shipped with. */
  musicVolume: 1,
  fxVolume: 1,
  /** Which take of the piece to play: 'loud' on the front end, 'ambient' in a match. */
  musicMood: 'loud',
  /**
   * Lo que se baja la música cuando se la retiene a propósito: 1 normalmente, menos en la pausa.
   *
   * Lo lee `trackLevel` (audio.js), que es por donde pasa todo volumen de la música.
   */
  musicDuck: 1,
  /** Gain node every one-off sound plays through, so a new level can cut them. */
  shotBus: null,
  /**
   * La salida del juego: música y efectos pasan por aquí antes de los altavoces.
   *
   * Existe porque la mezcla sale muy por debajo del techo (medido, 0,138 de pico en el bus de la
   * música con el volumen del jugador al 100 %: -17 dB), y eso obliga a subir el volumen del
   * equipo a tope para oírla, y a tope cualquier altavoz distorsiona en los golpes. Ver
   * `masterNode` en audio.js.
   */
  master: null,
  /** Half a second of white noise, generated once and reused by the whole kit. */
  noiseBuf: null,
  /**
   * One soft limiter for the music, built once and shared by every pass of the groove.
   * It belongs to the mix, not to a take, so it outlives the bus it was built for.
   */
  musicLimiter: null,
};

/**
 * El escaparate: las pantallas en las que no hay partida —el splash, la portada y los menús—.
 *
 * Ahí la ciudad y el cielo no son un tablero, son el anuncio de lo que se juega, y piden más vida
 * que una partida: la ciudad tiembla, sus ventanas van al doble, el cielo se ve siempre y pasa al
 * doble de ritmo, y los gorilas hacen más monerías. La pausa NO es escaparate: es una partida
 * parada y manda su modo, igual que el juego.
 *
 * Vive aquí y no en cada módulo que lo pregunta porque tres lo necesitan —city.js,
 * render/background.js y antics.js— y dos respuestas distintas a la misma pregunta serían dos
 * comportamientos distintos.
 */
const PLAYING_SCREENS = ['game', 'pause'];
export function isAttract() {
  return !PLAYING_SCREENS.includes(state.screen);
}
